import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { fetchData } from '../../../apiService';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, Chip, CircularProgress,
  InputAdornment, Pagination, Autocomplete, Popover
} from '@mui/material';
import { Close, Add, Delete, Search, List, Warning, KeyboardArrowDown, Check } from '@mui/icons-material';
import LogoLoader from '../../common/LogoLoader';

const EnquiryModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  enquiryDetails,
  setEnquiryDetails,
  onSave,
  items,
  brands = [],
  categories = [],
  loading,
  companyId,
  branchId,
  token,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Enquiry'
    : modalAction === 'edit' ? 'Edit Enquiry' : 'View Enquiry';

  // Data loss warning
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [originalDetails, setOriginalDetails] = useState([]);

  // Get Items modal
  const [getItemsModalOpen, setGetItemsModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [getItemsSearch, setGetItemsSearch] = useState('');
  const [currentItemsPage, setCurrentItemsPage] = useState(1);
  const [itemsPerPage] = useState(50);

  // Get Items — brand/category filter
  const [filterBrandId, setFilterBrandId] = useState(null);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [filteredByCategory, setFilteredByCategory] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);

  // Customer search
  const [customers, setCustomers] = useState([]);
  const [customerFound, setCustomerFound] = useState(false);

  // Offers/Warranty state
  const [itemOffersWarranty, setItemOffersWarranty] = useState({}); // { itemId: [offers] }
  const [offersModalOpen, setOffersModalOpen] = useState(false);
  const [offersModalData, setOffersModalData] = useState([]);
  const [offersModalItemName, setOffersModalItemName] = useState('');

  // Rate Selection (MOP) state — popover-based with progressive reveal
  const [rateModalOpen, setRateModalOpen] = useState(false);
  const [rateModalLoading, setRateModalLoading] = useState(false);
  const [rateOptions, setRateOptions] = useState([]);
  const [rateModalItemName, setRateModalItemName] = useState('');
  const [rateModalIndex, setRateModalIndex] = useState(null);

  // MOP popover
  const [mopAnchorEl, setMopAnchorEl] = useState(null);
  const [mopPopoverIndex, setMopPopoverIndex] = useState(null);
  const [mopRowOptions, setMopRowOptions] = useState([]);
  const [mopRowLoading, setMopRowLoading] = useState(false);
  const mopCacheRef = useRef({});

  // Build MOP options: rate = dealersPrice * MOPx% / 100 + dealersPrice
  const buildMopOptions = (itemData) => {
    const dealersPrice = parseFloat(itemData.DealersPrice) || 0;
    const options = [];
    const addMop = (label, pct) => {
      const p = parseFloat(pct) || 0;
      if (p > 0 && dealersPrice > 0) {
        options.push({ label, value: Math.round((dealersPrice * p / 100 + dealersPrice) * 100) / 100, pct: p });
      }
    };
    addMop('MOP1', itemData.MOP1);
    addMop('MOP2', itemData.MOP2);
    addMop('MOP3', itemData.MOP3);
    addMop('MOP4', itemData.MOP4);
    addMop('MOP5', itemData.MOP5);
    if (itemData.MRP && parseFloat(itemData.MRP) > 0)
      options.push({ label: 'MRP', value: parseFloat(itemData.MRP), pct: null });
    if (itemData.SaleRate && parseFloat(itemData.SaleRate) > 0)
      options.push({ label: 'Sale Rate', value: parseFloat(itemData.SaleRate), pct: null });
    return options;
  };

  // Progressive reveal: show options up to selected index + 1 next
  const getVisibleMopOptions = (allOptions, currentWhichMOP) => {
    if (!allOptions || allOptions.length === 0) return [];
    if (!currentWhichMOP) return allOptions.slice(0, 1);
    const selectedIdx = allOptions.findIndex(o => o.label === currentWhichMOP);
    if (selectedIdx === -1) return allOptions.slice(0, 1);
    return allOptions.slice(0, selectedIdx + 2);
  };

  const handleMopDropdownOpen = async (event, index) => {
    if (isViewMode) return;
    const detail = enquiryDetails[index];
    if (!detail.ItemId) return;
    event.stopPropagation();
    setMopAnchorEl(event.currentTarget);
    setMopPopoverIndex(index);
    if (mopCacheRef.current[detail.ItemId]) {
      setMopRowOptions(getVisibleMopOptions(mopCacheRef.current[detail.ItemId], detail.WhichMOP));
      setMopRowLoading(false);
      return;
    }
    setMopRowLoading(true);
    setMopRowOptions([]);
    try {
      const response = await fetchData('ItemApi', `/Item/${detail.ItemId}`, token);
      const data = response?.response_data || response || {};
      const itemData = data.Item || data;
      const allOptions = buildMopOptions(itemData);
      mopCacheRef.current[detail.ItemId] = allOptions;
      setMopRowOptions(getVisibleMopOptions(allOptions, detail.WhichMOP));
    } catch (e) {
      console.error('Error fetching MOP options:', e);
      setMopRowOptions([]);
    } finally {
      setMopRowLoading(false);
    }
  };

  const handleMopDropdownClose = () => {
    setMopAnchorEl(null);
    setMopPopoverIndex(null);
  };

  const handleMopSelect = (option) => {
    if (mopPopoverIndex !== null) {
      handleDetailChange(mopPopoverIndex, 'Rate', option.value.toString());
      setEnquiryDetails(prev => {
        const updated = [...prev];
        updated[mopPopoverIndex] = { ...updated[mopPopoverIndex], WhichMOP: option.label };
        return updated;
      });
    }
    handleMopDropdownClose();
  };

  // legacy — unused
  const handleRateClick = () => {};

  const handleOpenOffersModal = async (itemId) => {
    setOffersModalItemName(itemNameMap[itemId] || 'Item');
    setOffersModalData([]);
    setOffersModalOpen(true);
    // Fetch on click
    try {
      const response = await fetchData('ItemOffersAndWarrantyApi', `/ItemOffersAndWarrantyList/${itemId}`, token);
      const data = response.response_data || response || [];
      setOffersModalData(Array.isArray(data) ? data : [data]);
    } catch (e) {
      console.error('Error fetching offers/warranty:', e);
      setOffersModalData([]);
    }
  };

  // Fetch customers when modal opens
  useEffect(() => {
    if (open && companyId && token) {
      fetchData('ClientAndVendorApi', `/ClientList/${companyId}`, token)
        .then(res => setCustomers(res.response_data || res || []))
        .catch(() => setCustomers([]));
    }
  }, [open, companyId, token]);

  useEffect(() => {
    if (open && !isViewMode) {
      setOriginalFormData({ ...formData });
      setOriginalDetails([...enquiryDetails]);
      setHasUnsavedChanges(false);
    }
  }, [open, modalAction]);

  // Track changes
  const changeTrackRef = useRef(null);
  useEffect(() => {
    if (!isViewMode && Object.keys(originalFormData).length > 0) {
      clearTimeout(changeTrackRef.current);
      changeTrackRef.current = setTimeout(() => {
        const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
        const hasDetailChanges = JSON.stringify(enquiryDetails) !== JSON.stringify(originalDetails);
        setHasUnsavedChanges(hasFormChanges || hasDetailChanges);
      }, 300);
    }
    return () => clearTimeout(changeTrackRef.current);
  }, [formData, enquiryDetails, originalFormData, originalDetails, isViewMode]);

  const handleCloseClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleSave = async () => {
    try {
      await onSave();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Save failed:', error);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Phone number search — show matching customers in dropdown
  const [phoneSuggestions, setPhoneSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleSelectCustomer = (event, customer) => {
    if (customer) {
      setFormData(prev => ({
        ...prev,
        ContactNumber: customer.ContactNumber1 || '',
        CustomerName: customer.Name || customer.CustomerName || '',
        Address: customer.Address || customer.Address1 || '',
        MasterId: customer.MasterId || customer.Id || null
      }));
      setSelectedCustomer(customer);
      setCustomerFound(true);
    } else {
      setFormData(prev => ({ ...prev, ContactNumber: '', CustomerName: '', Address: '', MasterId: null }));
      setSelectedCustomer(null);
      setCustomerFound(false);
    }
  };

  // Calculate totals
  const calculateTotals = useCallback((details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0),
      TotalQuantity: acc.TotalQuantity + (parseFloat(d.Quantity) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, TotalQuantity: 0 });

    setFormData(prev => ({
      ...prev,
      TotalQuantity: totals.TotalQuantity,
      TotalAmount: Math.round(totals.TotalAmount * 100) / 100
    }));
  }, []);

  const handleDetailChange = useCallback((index, field, value) => {
    const updatedDetails = [...enquiryDetails];

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount'].includes(field)) {
      if (value === '' || value === null || value === undefined) {
        updatedDetails[index] = { ...updatedDetails[index], [field]: 0 };
        setEnquiryDetails(updatedDetails);
        calculateTotals(updatedDetails);
        return;
      }
      const validNumberRegex = /^\d*\.?\d*$/;
      if (!validNumberRegex.test(value)) return;
      const numValue = parseFloat(value);
      if (isNaN(numValue) || numValue < 0) return;
      if (field === 'DiscountPercentage' && numValue > 100) return;
    }

    updatedDetails[index] = { ...updatedDetails[index], [field]: value };

    if (['Quantity', 'Rate', 'DiscountPercentage', 'DiscountAmount', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const quantity = parseFloat(detail.Quantity) || 0;
      const rate = parseFloat(detail.Rate) || 0; // Rate is INCLUSIVE of tax
      const gstRate = parseFloat(detail.GSTRate) || 0;
      const amount = quantity * rate; // Total amount (tax inclusive)

      let discountAmount = 0, discountPct = 0;
      if (field === 'DiscountAmount') {
        discountAmount = Math.max(0, Math.min(parseFloat(value) || 0, amount));
        discountPct = amount > 0 ? (discountAmount / amount) * 100 : 0;
      } else {
        discountPct = Math.max(0, Math.min(parseFloat(detail.DiscountPercentage) || 0, 100));
        discountAmount = (amount * discountPct) / 100;
      }

      const netAmount = amount - discountAmount; // After discount (still inclusive of tax)
      // Back-calculate taxable from inclusive amount
      const taxableAmount = gstRate > 0 ? netAmount / (1 + gstRate / 100) : netAmount;
      const gstAmount = netAmount - taxableAmount;

      updatedDetails[index] = {
        ...updatedDetails[index],
        Amount: amount,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        IGSTAmount: 0,
        TotalAmount: Math.round(netAmount * 100) / 100
      };
    }

    setEnquiryDetails(updatedDetails);
    calculateTotals(updatedDetails);
  }, [enquiryDetails, calculateTotals]);

  const handleItemSelect = useCallback((index, itemId) => {
    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...enquiryDetails];
      const quantity = parseFloat(updatedDetails[index].Quantity) || 0;
      const rate = selectedItem.SaleRate || selectedItem.PurchaseRate || 0; // Rate is INCLUSIVE of tax
      const gstRate = selectedItem.GSTRate || 0;
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;

      const amount = quantity * rate; // Tax inclusive
      const discountAmount = (amount * discountPct) / 100;
      const netAmount = amount - discountAmount; // After discount, still inclusive
      // Back-calculate taxable from inclusive amount
      const taxableAmount = gstRate > 0 ? netAmount / (1 + gstRate / 100) : netAmount;
      const gstAmount = netAmount - taxableAmount;

      updatedDetails[index] = {
        ...updatedDetails[index],
        ItemId: itemId,
        HSNCode: selectedItem.HSNCode || '',
        StarRating: selectedItem.StarRating ||0,
        GSTRate: gstRate,
        Rate: rate,
        Amount: amount,
        DiscountPercentage: Math.round(discountPct * 100) / 100,
        DiscountAmount: Math.round(discountAmount * 100) / 100,
        TaxableAmount: Math.round(taxableAmount * 100) / 100,
        CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
        IGSTAmount: 0,
        TotalAmount: Math.round(netAmount * 100) / 100
      };
      setEnquiryDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [enquiryDetails, items, calculateTotals]);

  const fetchItemOffersWarranty = async (itemId) => {
    try {
      const response = await fetchData('ItemOffersAndWarrantyApi', `/ItemOffersAndWarrantyList/${itemId}`, token);
      const data = response.response_data || response || [];
      if (data && (Array.isArray(data) ? data.length > 0 : Object.keys(data).length > 0)) {
        setItemOffersWarranty(prev => ({ ...prev, [itemId]: Array.isArray(data) ? data : [data] }));
      }
    } catch (e) {
      console.error('Error fetching offers/warranty:', e);
    }
  };

  const addDetailRow = useCallback(() => {
    setEnquiryDetails(prev => [...prev, {
      Id: 0, ItemId: '', HSNCode: '', GSTRate: 0, Quantity: 0, Rate: 0, Amount: 0,
      DiscountPercentage: 0, DiscountAmount: 0, TaxableAmount: 0,
      CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, WhichMOP: null, StarRating: 0, IsActive: true
    }]);
  }, []);

  const removeDetailRow = useCallback((index) => {
    setEnquiryDetails(prev => {
      const updatedDetails = prev.filter((_, i) => i !== index);
      calculateTotals(updatedDetails);
      return updatedDetails;
    });
  }, [calculateTotals]);

  // Get Items Modal
  const handleOpenGetItemsModal = async () => {
    setSelectedItems([]);
    setGetItemsSearch('');
    setCurrentItemsPage(1);
    setFilteredByCategory([]);
    // If brand or category selected, fetch filtered items first then open
    if (filterBrandId || filterCategoryId) {
      const bId = filterBrandId || 0;
      const cId = filterCategoryId || 0;
      setFilterLoading(true);
      try {
        const res = await fetchData('SchemeApi', `/BrandAndCategoryWiseItemList/${companyId}/${branchId}/${bId}/${cId}`, token);
        setFilteredByCategory(res.response_data || res || []);
      } catch (e) {
        setFilteredByCategory([]);
      } finally {
        setFilterLoading(false);
      }
    }
    setGetItemsModalOpen(true);
  };

  const handleCloseGetItemsModal = () => {
    setGetItemsModalOpen(false);
    setSelectedItems([]);
    setGetItemsSearch('');
    setFilteredByCategory([]);
    // Keep filterBrandId / filterCategoryId so user can reuse same filter
  };

  // filtered items source: brand/category filter active → use filteredByCategory, else full items


  const handleItemSelection = (item, isSelected) => {
    if (isSelected) {
      setSelectedItems(prev => [...prev, {
        ...item,
        selectedQuantity: 1,
        selectedRate: item.SaleRate || 0,
        selectedDiscountPercentage: 0
      }]);
    } else {
      setSelectedItems(prev => prev.filter(s => s.Id !== item.Id));
    }
  };

  const handleAddSelectedItems = () => {
    if (selectedItems.length === 0) return;
    const newItems = selectedItems
      .filter(si => !enquiryDetails.some(d => d.ItemId === si.Id))
      .map(si => {
        const quantity = parseFloat(si.selectedQuantity) || 0;
        const rate = parseFloat(si.selectedRate) || 0; // Rate is INCLUSIVE of tax
        const gstRate = parseFloat(si.GSTRate) || 0;
        const discPct = parseFloat(si.selectedDiscountPercentage) || 0;
        const amount = quantity * rate; // Tax inclusive
        const discountAmount = (amount * discPct) / 100;
        const netAmount = amount - discountAmount; // After discount, still inclusive
        // Back-calculate taxable from inclusive amount
        const taxableAmount = gstRate > 0 ? netAmount / (1 + gstRate / 100) : netAmount;
        const gstAmount = netAmount - taxableAmount;

        return {
          Id: 0, ItemId: si.Id, HSNCode: si.HSNCode || '', GSTRate: gstRate,StarRating : si.StarRating,
          Quantity: quantity, Rate: rate, Amount: amount,
          DiscountPercentage: discPct, DiscountAmount: Math.round(discountAmount * 100) / 100,
          TaxableAmount: Math.round(taxableAmount * 100) / 100,
          CGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          SGSTAmount: Math.round((gstAmount / 2) * 100) / 100,
          IGSTAmount: 0,
          TotalAmount: Math.round(netAmount * 100) / 100, WhichMOP: null, IsActive: true
        };
      });

    const updatedDetails = [...enquiryDetails, ...newItems];
    setEnquiryDetails(updatedDetails);
    calculateTotals(updatedDetails);
    handleCloseGetItemsModal();
  };

  // Filtered items for Get Items modal
  const filteredItems = useMemo(() => {
    // Source: if brand/category filter active use filtered list, else use full items list
    const source = (filterBrandId || filterCategoryId) ? filteredByCategory : items;
    if (!getItemsSearch.trim()) return source;
    const term = getItemsSearch.toLowerCase().trim();
    return source.filter(item =>
      item.Name?.toLowerCase().includes(term) ||
      item.HSNCode?.toLowerCase().includes(term) ||
      item.Code?.toLowerCase().includes(term)
    );
  }, [items, filteredByCategory, filterBrandId, filterCategoryId, getItemsSearch]);

  const paginatedItems = useMemo(() => {
    const start = (currentItemsPage - 1) * itemsPerPage;
    return filteredItems.slice(start, start + itemsPerPage);
  }, [filteredItems, currentItemsPage, itemsPerPage]);

  const totalItemPages = Math.ceil(filteredItems.length / itemsPerPage);

  const isItemInDetails = (itemId) => enquiryDetails.some(d => d.ItemId === parseInt(itemId));

  // Item name map
  const itemNameMap = useMemo(() => {
    const map = {};
    items.forEach(i => { map[i.Id] = i.Code ? `${i.Code} - ${i.Name}` : i.Name; });
    return map;
  }, [items]);

  const formatCurrency = useCallback((amt) =>
    `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, []);

  const compactInput = {
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' },
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } })
  };
  const tinyInput = {
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.7rem', textAlign: 'right', fontWeight: 700 },
    minWidth: 0,
    ...(isViewMode && { '& .MuiInputBase-input.Mui-disabled': { color: '#5D4037', fontWeight: 700, WebkitTextFillColor: '#5D4037' } })
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) { setCancelConfirmOpen(true); return; }
          }
          onClose();
        }}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2, position: 'relative' } }}
      >
        {/* Page loader — shows when data is being fetched */}
        <LogoLoader open={loading} message="Loading..." />

        {/* Header */}
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: isMobile ? '0.9rem' : '1rem' }}>
                {title}
              </Typography>
              {hasUnsavedChanges && !isViewMode && (
                <Chip label="Unsaved" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }} />
              )}
            </Box>
            <IconButton size="small" onClick={handleCloseClick}><Close /></IconButton>
          </Box>
        </DialogTitle>

        {/* Content */}
        <DialogContent sx={{ p: 2, overflow: 'auto' }}>
          {/* Customer Info Section */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af', mb: 1, display: 'block' }}>
              ENQUIRY DETAILS
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Enquiry No" value={formData.EnquiryNo || ''} disabled sx={compactInput} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField fullWidth size="small" label="Date" type="date" value={formData.EnquiryDate || ''} disabled={isViewMode}
                  onChange={(e) => handleChange('EnquiryDate', e.target.value)} sx={compactInput} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Customer Name *" value={formData.CustomerName || ''} disabled={isViewMode}
                  onChange={(e) => handleChange('CustomerName', e.target.value)} sx={compactInput} placeholder="Enter customer name" />
              </Grid>

              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={customers}
                  value={selectedCustomer}
                  onChange={handleSelectCustomer}
                  disabled={isViewMode}
                  getOptionLabel={(option) => {
                    const phone = option.ContactNumber1 || '';
                    const name = option.Name || option.CustomerName || '';
                    return `${phone} - ${name}`;
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const term = inputValue.toLowerCase();
                    return options.filter(c =>
                      (c.ContactNumber1 || '').includes(term) ||
                      (c.Name || c.CustomerName || '').toLowerCase().includes(term)
                    ).slice(0, 15);
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.5, px: 1.5 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#1e293b' }}>
                        {option.Name || option.CustomerName || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#64748b' }}>
                        📞 {option.ContactNumber1 || '-'}
                      </Typography>
                    </Box>
                  )}
                  freeSolo
                  onInputChange={(event, value, reason) => {
                    if (reason === 'input') {
                      const numericValue = value.replace(/[^0-9]/g, '');
                      setFormData(prev => ({ ...prev, ContactNumber: numericValue, MasterId: null }));
                      setCustomerFound(false);
                      setSelectedCustomer(null);
                    }
                  }}
                  inputValue={formData.ContactNumber || ''}
                  renderInput={(params) => (
                    <TextField {...params} size="small" label="Contact Number *"
                      placeholder="Search phone/name" sx={compactInput}
                      helperText={customerFound ? '✓ Selected' : ''}
                      FormHelperTextProps={{ sx: { color: '#059669', fontSize: '0.6rem', mt: 0.1 } }}
                      inputProps={{ ...params.inputProps, inputMode: 'numeric', maxLength: 15 }} />
                  )}
                  noOptionsText="Not found — type manually"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <TextField fullWidth size="small" label="Address" value={formData.Address || ''} disabled={isViewMode}
                  onChange={(e) => handleChange('Address', e.target.value)} sx={compactInput} placeholder="Enter address" />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          {/* Item Details Section */}
          <Box>
            {/* Mobile: card layout for Brand/Category/Get Items */}
            {isMobile && !isViewMode ? (
              <Box sx={{ mb: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af' }}>
                    ITEMS ({enquiryDetails.length})
                  </Typography>
                </Box>

                {/* Category + Get Items button side by side */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Autocomplete
                    options={categories}
                    value={categories.find(c => c.Id === filterCategoryId) || null}
                    onChange={(e, val) => setFilterCategoryId(val ? val.Id : null)}
                    getOptionLabel={(option) => option.Name || ''}
                    size="small"
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Category" placeholder="Search category..."
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }} />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ fontSize: '0.8rem', py: 0.5, px: 1.5 }}>{option.Name}</Box>
                    )}
                    noOptionsText="No categories"
                    clearOnEscape
                  />

                  {/* Brand */}
                  <Autocomplete
                    options={brands}
                    value={brands.find(b => b.Id === filterBrandId) || null}
                    onChange={(e, val) => setFilterBrandId(val ? val.Id : null)}
                    getOptionLabel={(option) => option.Name || ''}
                    size="small"
                    fullWidth
                    sx={{ mb: 1 }}
                    renderInput={(params) => (
                      <TextField {...params} label="Brand" placeholder="Search brand..."
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' }, '& .MuiInputLabel-root': { fontSize: '0.8rem' } }} />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props} sx={{ fontSize: '0.8rem', py: 0.5, px: 1.5 }}>{option.Name}</Box>
                    )}
                    noOptionsText="No brands"
                    clearOnEscape
                  />

                  <Button
                    size="small"
                    variant="contained"
                    startIcon={filterLoading ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : <List />}
                    onClick={handleOpenGetItemsModal}
                    disabled={filterLoading}
                    sx={{ fontSize: '0.75rem', py: 1, px: 1.5, textTransform: 'none', bgcolor: '#1e3a6e', '&:hover': { bgcolor: '#1a2d5a' }, whiteSpace: 'nowrap', borderRadius: 1.5, flexShrink: 0 }}
                  >
                    Get Items
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af' }}>
                  ITEM DETAILS
                </Typography>
                {!isViewMode && (
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Brand filter — searchable */}
                    <Autocomplete
                      options={brands}
                      value={brands.find(b => b.Id === filterBrandId) || null}
                      onChange={(e, val) => setFilterBrandId(val ? val.Id : null)}
                      getOptionLabel={(option) => option.Name || ''}
                      size="small"
                      sx={{ minWidth: 140 }}
                      renderInput={(params) => (
                        <TextField {...params} label="Brand" placeholder="Search brand..."
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: '2px !important' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }} />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ fontSize: '0.75rem', py: 0.4, px: 1.5 }}>{option.Name}</Box>
                      )}
                      noOptionsText="No brands"
                      clearOnEscape
                    />
                    {/* Category filter — searchable */}
                    <Autocomplete
                      options={categories}
                      value={categories.find(c => c.Id === filterCategoryId) || null}
                      onChange={(e, val) => setFilterCategoryId(val ? val.Id : null)}
                      getOptionLabel={(option) => option.Name || ''}
                      size="small"
                      sx={{ minWidth: 140 }}
                      renderInput={(params) => (
                        <TextField {...params} label="Category" placeholder="Search category..."
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', py: '2px !important' }, '& .MuiInputLabel-root': { fontSize: '0.75rem' } }} />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ fontSize: '0.75rem', py: 0.4, px: 1.5 }}>{option.Name}</Box>
                      )}
                      noOptionsText="No categories"
                      clearOnEscape
                    />
                    <Button size="small" variant="outlined" startIcon={<List />} onClick={handleOpenGetItemsModal}
                      sx={{ fontSize: '0.7rem', py: 0.5, textTransform: 'none' }}>
                      Get Items
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* Item Grid Table / Mobile Cards */}
            {isMobile ? (
              /* Mobile: card-per-item layout */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, border: enquiryDetails.length === 0 ? '1.5px dashed #cbd5e1' : 'none', borderRadius: 1.5, p: enquiryDetails.length === 0 ? 2 : 0 }}>
                {enquiryDetails.length === 0 ? (
                  <Typography sx={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem', py: 2 }}>
                    No items added. Tap "Get Items" to add.
                  </Typography>
                ) : (
                  enquiryDetails.map((detail, index) => (
                    <Box key={index} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      {/* Item header row: number + name + delete */}
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
                          <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: '#1e3a6e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Typography sx={{ fontSize: '0.6rem', color: '#fff', fontWeight: 700 }}>{index + 1}</Typography>
                          </Box>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                            {itemNameMap[detail.ItemId] || <em style={{ color: '#94a3b8', fontWeight: 400 }}>No item selected</em>}
                          </Typography>
                        </Box>
                        {!isViewMode && (
                          <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ color: '#dc2626', p: 0.3, ml: 0.5 }}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        )}
                      </Box>
                      {/* Offers chip + Star Rating */}
                      {detail.ItemId && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1 }}>
                          <Chip size="small" label="🎁 Offers/Warranty"
                            onClick={() => handleOpenOffersModal(detail.ItemId)}
                            sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', cursor: 'pointer', width: 'fit-content' }}
                          />
                          {/* Star Rating (readonly) */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span
                                key={star}
                                style={{
                                  fontSize: '0.75rem',
                                  color: star <= (detail.StarRating || 0) ? '#f59e0b' : '#d1d5db',
                                  cursor: 'default',
                                  lineHeight: 1,
                                }}
                              >
                                ★
                              </span>
                            ))}
                            {detail.StarRating > 0 && (
                              <Typography sx={{ fontSize: '0.55rem', color: '#92400e', fontWeight: 700, ml: 0.2 }}>
                                {detail.StarRating}/5
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                      {/* Fields row */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.3, textTransform: 'uppercase', fontWeight: 600 }}>Qty</Typography>
                          <TextField size="small" value={detail.Quantity || ''} disabled={isViewMode}
                            onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)}
                            sx={{ '& .MuiInputBase-input': { py: 0.6, px: 0.75, fontSize: '0.8rem', textAlign: 'center', fontWeight: 700 } }}
                            fullWidth
                          />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.3, textTransform: 'uppercase', fontWeight: 600 }}>Rate</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <TextField size="small" value={detail.Rate || ''} disabled={isViewMode}
                              onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                              sx={{ '& .MuiInputBase-input': { py: 0.6, px: 0.75, fontSize: '0.8rem', textAlign: 'center', fontWeight: 700 } }}
                              fullWidth
                            />
                            {!isViewMode && detail.ItemId && (
                              <button
                                onClick={(e) => handleMopDropdownOpen(e, index)}
                                title="Select MOP"
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  width: 26, height: 33, padding: 0, border: 'none',
                                  borderRadius: 6, cursor: 'pointer', flexShrink: 0,
                                  background: detail.WhichMOP ? '#1e40af' : '#e0e7ff',
                                  color: detail.WhichMOP ? '#fff' : '#1e40af',
                                  fontSize: '0.55rem', fontWeight: 700,
                                }}
                              >
                                {detail.WhichMOP
                                  ? detail.WhichMOP.replace('MOP', 'M').replace('Sale Rate', 'SR')
                                  : <KeyboardArrowDown style={{ fontSize: 13 }} />}
                              </button>
                            )}
                          </Box>
                          {detail.WhichMOP && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 2,
                              fontSize: '0.55rem', fontWeight: 700, padding: '1px 5px',
                              borderRadius: 8, background: '#dbeafe', color: '#1e40af',
                              border: '1px solid #93c5fd', marginTop: 2,
                            }}>
                              ✓ {detail.WhichMOP}
                              {!isViewMode && (
                                <span onClick={() => setEnquiryDetails(prev => { const u=[...prev]; u[index]={...u[index],WhichMOP:null}; return u; })}
                                  style={{ cursor: 'pointer', color: '#6b7280', fontWeight: 400 }}>×</span>
                              )}
                            </span>
                          )}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: '0.6rem', color: '#64748b', mb: 0.3, textTransform: 'uppercase', fontWeight: 600 }}>GST%</Typography>
                          <Box sx={{ height: 33, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1 }}>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#059669' }}>{detail.GSTRate || 0}%</Typography>
                          </Box>
                        </Box>
                      </Box>
                      {/* Amount footer */}
                      <Box sx={{ mt: 1, pt: 0.75, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.7rem', color: '#64748b' }}>Amount</Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e40af' }}>
                          ₹{(parseFloat(detail.Amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            ) : (
              /* Desktop: table layout */
              <Box sx={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(135deg, #1e3a6e, #2c4f8c)' }}>
                      <th style={thStyle}>#</th>
                      <th style={{ ...thStyle, minWidth: 180 }}>Item</th>
                      {/* <th style={thStyle}>HSN</th> */}
                      <th style={thStyle}>Qty</th>
                      <th style={thStyle}>Rate</th>
                      <th style={thStyle}>GST%</th>
                      <th style={thStyle}>Amount</th>
                      {/* <th style={thStyle}>Disc%</th>
                    <th style={thStyle}>Disc Amt</th>
                    <th style={thStyle}>Taxable</th> */}
                      {/* <th style={thStyle}>GST%</th> */}
                      {/* <th style={thStyle}>CGST</th>
                    <th style={thStyle}>SGST</th>
                    <th style={thStyle}>Total</th> */}
                      {!isViewMode && <th style={thStyle}>×</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {enquiryDetails.length === 0 ? (
                      <tr>
                        <td colSpan={isViewMode ? 13 : 14} style={{ textAlign: 'center', padding: '16px', color: '#94a3b8' }}>
                          No items added. Click "Add Row" or "Get Items" to begin.
                        </td>
                      </tr>
                    ) : (

                      enquiryDetails.map((detail, index) => (
                        <tr key={index} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={tdStyle}>{index + 1}</td>
                          <td style={tdStyle}>
                            {isViewMode || detail.ItemId ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                <span style={{ fontSize: '0.7rem' }}>
                                  {itemNameMap[detail.ItemId] || '-'}
                                </span>
                                {detail.ItemId && (
                                  <Chip size="small" label="🎁 Offers/Warranty"
                                    onClick={() => handleOpenOffersModal(detail.ItemId)}
                                    sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', cursor: 'pointer', width: 'fit-content' }}
                                  />
                                )}
                                {/* Star Rating (readonly) */}
                                {detail.ItemId && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.2 }}>
                                    {[1, 2, 3, 4, 5].map((star) => (
                                      <span
                                        key={star}
                                        style={{
                                          fontSize: '0.72rem',
                                          color: star <= (detail.StarRating || 0) ? '#f59e0b' : '#d1d5db',
                                          cursor: 'default',
                                          lineHeight: 1,
                                        }}
                                      >
                                        ★
                                      </span>
                                    ))}
                                    {detail.StarRating > 0 && (
                                      <span style={{ fontSize: '0.5rem', color: '#92400e', fontWeight: 700, marginLeft: 2 }}>
                                        {detail.StarRating}/5
                                      </span>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <FormControl fullWidth size="small" sx={{ minWidth: 160 }}>
                                <Select value={detail.ItemId || ''} onChange={(e) => handleItemSelect(index, e.target.value)}
                                  sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5, px: 0.5 } }}>
                                  <MenuItem value="" disabled><em>Select Item</em></MenuItem>
                                  {items.map(item => (
                                    <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.75rem' }}>
                                      {item.Code ? `${item.Code} - ${item.Name}` : item.Name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </td>
                          {/* <td style={tdStyle}>
                          <TextField size="small" value={detail.HSNCode || ''} disabled sx={tinyInput} style={{ width: 60 }} />
                        </td> */}
                          <td style={tdStyle}>
                            <TextField size="small" value={detail.Quantity || ''} disabled={isViewMode}
                              onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} sx={tinyInput} style={{ width: 55 }} />
                          </td>
                          <td style={tdStyle}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TextField size="small" value={detail.Rate || ''} disabled={isViewMode}
                                onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)}
                                sx={tinyInput} style={{ width: 70 }} />
                              {!isViewMode && detail.ItemId && (
                                <button
                                  onClick={(e) => handleMopDropdownOpen(e, index)}
                                  title="Select MOP"
                                  style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 22, height: 22, padding: 0, border: 'none',
                                    borderRadius: 5, cursor: 'pointer', flexShrink: 0,
                                    background: detail.WhichMOP ? '#1e40af' : '#e0e7ff',
                                    color: detail.WhichMOP ? '#fff' : '#1e40af',
                                    fontSize: '0.5rem', fontWeight: 700,
                                  }}
                                >
                                  {detail.WhichMOP
                                    ? detail.WhichMOP.replace('MOP', 'M').replace('Sale Rate', 'SR')
                                    : <KeyboardArrowDown style={{ fontSize: 12 }} />}
                                </button>
                              )}
                            </Box>
                            {detail.WhichMOP && (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 2,
                                fontSize: '0.5rem', fontWeight: 700, padding: '1px 4px',
                                borderRadius: 6, background: '#dbeafe', color: '#1e40af',
                                border: '1px solid #93c5fd', marginTop: 2,
                              }}>
                                ✓ {detail.WhichMOP}
                                {!isViewMode && (
                                  <span onClick={() => setEnquiryDetails(prev => { const u=[...prev]; u[index]={...u[index],WhichMOP:null}; return u; })}
                                    style={{ cursor: 'pointer', color: '#6b7280', fontWeight: 400 }}>×</span>
                                )}
                              </span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            <span style={{ fontSize: '0.7rem', fontWeight: 600 }}>{detail.GSTRate || 0}%</span>
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 600 }}>
                            {(parseFloat(detail.Amount) || 0).toFixed(2)}
                          </td>
                          {/* <td style={tdStyle}>
                          <TextField size="small" value={detail.DiscountPercentage || ''} disabled={isViewMode}
                            onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} sx={tinyInput} style={{ width: 50 }} />
                        </td>
                        <td style={tdStyle}>
                          <TextField size="small" value={detail.DiscountAmount || ''} disabled={isViewMode}
                            onChange={(e) => handleDetailChange(index, 'DiscountAmount', e.target.value)} sx={tinyInput} style={{ width: 65 }} />
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                          {(parseFloat(detail.TaxableAmount) || 0).toFixed(2)}
                        </td> */}
                          {/* <td style={tdStyle}>
                          <span style={{ fontSize: '0.7rem', fontWeight: 600 }}>{detail.GSTRate || 0}%</span>
                        </td> */}
                          {/* <td style={{ ...tdStyle, color: '#059669' }}>
                          {(parseFloat(detail.CGSTAmount) || 0).toFixed(2)}
                        </td>
                        <td style={{ ...tdStyle, color: '#059669' }}>
                          {(parseFloat(detail.SGSTAmount) || 0).toFixed(2)}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 700, color: '#1e40af' }}>
                          {(parseFloat(detail.TotalAmount) || 0).toFixed(2)}
                        </td> */}
                          {!isViewMode && (
                            <td style={tdStyle}>
                              <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ color: '#dc2626', p: 0.3 }}>
                                <Delete sx={{ fontSize: 14 }} />
                              </IconButton>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
            )} {/* end desktop table / mobile ternary */}

            {/* Totals */}
            {enquiryDetails.length > 0 && (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ minWidth: 280, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, p: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Total Quantity:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700 }}>{formData.TotalQuantity || 0}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Taxable Amount:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatCurrency(enquiryDetails.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0))}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>Total GST (CGST + SGST):</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669' }}>
                      {formatCurrency(enquiryDetails.reduce((sum, d) => sum + (parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0), 0))}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e40af' }}>Grand Total:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.85rem' }}>
                      {formatCurrency(formData.TotalAmount)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseClick} variant="outlined" size="small" sx={{ textTransform: 'none' }}>
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          {!isViewMode && (
            <Button onClick={handleSave} variant="contained" size="small" disabled={loading}
              sx={{ textTransform: 'none', bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}>
              {loading ? <CircularProgress size={16} /> : (modalAction === 'create' ? 'Create Enquiry' : 'Update Enquiry')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation */}
      <Dialog open={cancelConfirmOpen} onClose={() => setCancelConfirmOpen(false)} maxWidth="xs">
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#d97706' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Unsaved Changes</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">You have unsaved changes. Are you sure you want to discard them?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)} size="small">Stay</Button>
          <Button onClick={handleConfirmCancel} color="error" size="small" variant="contained">Discard</Button>
        </DialogActions>
      </Dialog>

      {/* Get Items Modal */}
      <Dialog
        open={getItemsModalOpen}
        onClose={handleCloseGetItemsModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' } }}
      >
        {/* Header */}
        <DialogTitle sx={{ py: 1.5, px: 2.5, borderBottom: '1px solid #f0f0f0', bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Select Items</Typography>
            <IconButton size="small" onClick={handleCloseGetItemsModal}
              sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' }, borderRadius: '8px' }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Search bar */}
          <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search items by name, code or HSN.."
              value={getItemsSearch}
              onChange={(e) => { setGetItemsSearch(e.target.value); setCurrentItemsPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 17, color: '#94a3b8' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#f8fafc',
                  fontSize: '0.85rem',
                  '& fieldset': { borderColor: '#e2e8f0' },
                  '&:hover fieldset': { borderColor: '#cbd5e1' },
                  '&.Mui-focused fieldset': { borderColor: '#2C3E6E' },
                },
              }}
            />
          </Box>

          {/* Table */}
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '9px 14px', fontWeight: 700, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', width: 40 }}>✓</th>
                  {/* <th style={{ padding: '9px 14px', fontWeight: 700, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' }}>Code</th> */}
                  <th style={{ padding: '9px 14px', fontWeight: 700, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' }}>Item Name</th>
                  {/* <th style={{ padding: '9px 14px', fontWeight: 700, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' }}>HSN</th> */}
                  <th style={{ padding: '9px 14px', fontWeight: 700, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Rate</th>
                  <th style={{ padding: '9px 14px', fontWeight: 700, color: '#475569', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>GST%</th>
                </tr>
              </thead>
              <tbody>
                {filterLoading ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '0.85rem' }}>
                      <CircularProgress size={22} sx={{ color: '#2C3E6E', mb: 1, display: 'block', mx: 'auto' }} />
                      Loading items...
                    </td>
                  </tr>
                ) : filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '0.85rem' }}>
                      No items found
                    </td>
                  </tr>
                ) : (
                  paginatedItems.map((item, idx) => {
                    const isSelected = selectedItems.some(s => s.Id === item.Id);
                    const isInDetails = isItemInDetails(item.Id);
                    return (
                      <tr
                        key={item.Id}
                        onClick={() => !isInDetails && handleItemSelection(item, !isSelected)}
                        style={{
                          borderBottom: '1px solid #f1f5f9',
                          background: isSelected ? '#eff6ff' : idx % 2 === 0 ? '#fff' : '#fafbfc',
                          opacity: isInDetails ? 0.45 : 1,
                          cursor: isInDetails ? 'not-allowed' : 'pointer',
                          transition: 'background 0.1s',
                        }}
                      >
                        <td style={{ padding: '9px 14px', textAlign: 'left' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isInDetails}
                            onChange={(e) => handleItemSelection(item, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: 14, height: 14, accentColor: '#2C3E6E', cursor: isInDetails ? 'not-allowed' : 'pointer' }}
                          />
                        </td>
                        {/* <td style={{ padding: '9px 14px', color: '#64748b', fontWeight: 500 }}>{item.Code || '-'}</td> */}
                        <td style={{ padding: '9px 14px', color: '#1e293b', fontWeight: isSelected ? 600 : 400 }}>
                          <div>{item.Name}</div>
                          {/* Star Rating (readonly) */}
                          {item.StarRating > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '3px' }}>
                              {[1, 2, 3, 4, 5].map(star => (
                                <span key={star} style={{ fontSize: '0.72rem', color: star <= item.StarRating ? '#f59e0b' : '#d1d5db', lineHeight: 1 }}>★</span>
                              ))}
                              <span style={{ fontSize: '0.6rem', color: '#92400e', fontWeight: 700, marginLeft: 2 }}>{item.StarRating}/5</span>
                            </div>
                          )}
                        </td>
                        {/* <td style={{ padding: '9px 14px', color: '#64748b' }}>{item.HSNCode || '-'}</td> */}
                        <td style={{ padding: '9px 14px', color: '#1e293b', textAlign: 'right', fontWeight: 600 }}>
                          {(() => {
                            const mop1 = parseFloat(item.MOP1) || 0;
                            return mop1 > 0 ? mop1 : (parseFloat(item.MOPRate) || 0);
                          })()}
                        </td>
                        <td style={{ padding: '9px 14px', color: '#059669', textAlign: 'right', fontWeight: 600 }}>{item.GSTRate || 0}%</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>

          {/* Pagination */}
          {totalItemPages > 1 && (
            <Box sx={{ px: 2, pt: 1, display: 'flex', justifyContent: 'center' }}>
              <Pagination count={totalItemPages} page={currentItemsPage} onChange={(_, p) => setCurrentItemsPage(p)} size="small" />
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #f0f0f0', bgcolor: '#fff', gap: 1 }}>
          {selectedItems.length > 0 && (
            <Typography variant="caption" sx={{ flex: 1, color: '#059669', fontWeight: 600, fontSize: '0.78rem' }}>
              {selectedItems.length} item(s) selected
            </Typography>
          )}
          <Button
            onClick={handleCloseGetItemsModal}
            size="small"
            variant="outlined"
            sx={{ textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px', borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddSelectedItems}
            size="small"
            variant="contained"
            disabled={selectedItems.length === 0}
            sx={{ textTransform: 'none', fontSize: '0.8rem', borderRadius: '8px', bgcolor: '#2C3E6E', '&:hover': { bgcolor: '#1a2d5a' }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' } }}
          >
            Add Items{selectedItems.length > 0 ? ` (${selectedItems.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Offers/Warranty Modal */}
      <Dialog open={offersModalOpen} onClose={() => setOffersModalOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3a6e 0%, #2c4f8c 100%)', px: 2.5, py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#fff' }}>🎁 Offers & Warranty</Typography>
              <Typography sx={{ fontSize: '0.8rem', color: '#cbd5e1', mt: 0.3 }}>{offersModalItemName}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setOffersModalOpen(false)} sx={{ color: '#fff', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
              <Close />
            </IconButton>
          </Box>
        </Box>
        <DialogContent sx={{ p: 2.5, bgcolor: '#f8fafc' }}>
          {offersModalData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography sx={{ fontSize: '2rem', mb: 1 }}>📭</Typography>
              <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>No offers or warranty available for this item.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {offersModalData.map((item, idx) => (
                <Box key={idx} sx={{
                  p: 2, borderRadius: '12px', bgcolor: '#fff',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  transition: 'transform 0.1s, box-shadow 0.1s',
                  '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }
                }}>
                  <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: '1.1rem' }}>🏷️</span>
                  </Box>
                  <Typography sx={{ fontSize: '0.88rem', color: '#1e293b', fontWeight: 500, lineHeight: 1.4 }}>
                    {item.OffersAndWarranty || '-'}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* MOP Dropdown Popover */}
      <Popover
        open={Boolean(mopAnchorEl)}
        anchorEl={mopAnchorEl}
        onClose={handleMopDropdownClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(30,64,175,0.18)',
            border: '1px solid #dbeafe',
            minWidth: 210,
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', px: 1.75, py: 1.1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#fff' }}>
            💰 Select Rate (MOP)
          </Typography>
          {mopPopoverIndex !== null && (
            <Typography sx={{ fontSize: '0.65rem', color: '#bfdbfe', mt: 0.2 }} noWrap>
              {itemNameMap[enquiryDetails[mopPopoverIndex]?.ItemId] || ''}
            </Typography>
          )}
        </Box>
        <Box sx={{ py: 0.5 }}>
          {mopRowLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2.5, gap: 1 }}>
              <CircularProgress size={16} sx={{ color: '#1e40af' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Loading...</Typography>
            </Box>
          ) : mopRowOptions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>No rate options available</Typography>
            </Box>
          ) : (
            mopRowOptions.map((option, idx) => {
              const currentWhichMOP = mopPopoverIndex !== null ? enquiryDetails[mopPopoverIndex]?.WhichMOP : null;
              const isSelected = currentWhichMOP === option.label;
              const isNextReveal = !isSelected && idx === mopRowOptions.length - 1 && currentWhichMOP !== null;
              const shortLabel = option.label.replace('MOP', 'M').replace('Sale Rate', 'SR');
              return (
                <Box
                  key={idx}
                  onClick={() => handleMopSelect(option)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    px: 1.75, py: 0.85, cursor: 'pointer',
                    bgcolor: isSelected ? '#eff6ff' : isNextReveal ? '#fefce8' : 'transparent',
                    borderLeft: isSelected ? '3px solid #1e40af' : isNextReveal ? '3px solid #f59e0b' : '3px solid transparent',
                    transition: 'background 0.12s',
                    '&:hover': { bgcolor: isNextReveal ? '#fef9c3' : '#f0f9ff' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 28, height: 28, borderRadius: '7px',
                      bgcolor: isSelected ? '#1e40af' : isNextReveal ? '#fef3c7' : '#e0e7ff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: isSelected ? '#fff' : isNextReveal ? '#92400e' : '#1e40af' }}>
                        {shortLabel}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: isSelected ? 700 : 500, color: '#1e293b', lineHeight: 1.2 }}>
                        {option.label}
                      </Typography>
                      {isNextReveal && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#92400e', fontWeight: 600 }}>
                          {option.label === 'MRP' ? 'actual rate' : 'lower rate'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#059669', lineHeight: 1.2 }}>
                        ₹{option.value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                       {/*{option.pct != null && (
                        <Typography sx={{ fontSize: '0.6rem', color: '#6b7280', fontWeight: 600, lineHeight: 1 }}>
                          {option.pct}%
                        </Typography>
                      )} */}
                    </Box>
                    {isSelected && <Check sx={{ fontSize: 14, color: '#1e40af' }} />}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Popover>
    </>
  );
};

// Table styles
const thStyle = {
  padding: '8px 6px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#e2e8f0',
  fontSize: '0.65rem',
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '4px 6px',
  fontSize: '0.7rem',
  verticalAlign: 'middle'
};

export default EnquiryModal;
